import { AccessTime, Language, Public } from "@mui/icons-material";
import { Alert, Autocomplete, Box, Button, Card, CircularProgress, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { colors } from "../../../../../../ui/colors";
import { useUpdateCompany } from "../../../../../hooks/useCompany";

export interface ITimezoneConfigModalContentProps {
  initialValue?: string | null;
  onSave: (payload: { timeZone: string }) => Promise<void> | void;
  onCancel?: () => void;
  saving?: boolean;
}

export interface ICompanyTimezoneModalContainerProps {
  initialTimeZone?: string | null;
  onSaved?: (payload: { timeZone: string }) => Promise<void> | void;
  onClose?: () => void;
}

const compactFieldHeight = 56;

const compactInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    height: compactFieldHeight,
    "& fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: colors.orange[200],
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontWeight: 500,
    fontSize: "0.9rem",
    "&.Mui-focused": {
      color: colors.orange.base,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 12px",
    fontSize: "0.95rem",
    color: colors.gray.base,
    boxSizing: "border-box",
  },
};

const compactAutocompleteSx = {
  ...compactInputSx,
  "& .MuiAutocomplete-root": {
    height: compactFieldHeight,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    height: compactFieldHeight,
    paddingRight: "40px !important",
    alignItems: "center",
    "& fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: colors.orange[200],
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
    },
  },
  "& .MuiAutocomplete-input": {
    padding: "7.5px 4px !important",
    minWidth: 0,
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "calc(50% - 14px)",
  },
  "& .MuiSvgIcon-root": {
    color: colors.orange.base,
  },
};

const compactInfoPillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.1,
  py: 0.7,
  borderRadius: "999px",
  backgroundColor: colors.orange[50],
  border: `1px solid ${colors.orange[200] || "#ffd7ad"}`,
};

const compactActionButtonSx = {
  borderRadius: "8px",
  px: 2.25,
  py: 0.9,
  fontSize: "0.9rem",
  fontWeight: 600,
  textTransform: "none" as const,
};

const compactCancelButtonSx = {
  ...compactActionButtonSx,
  color: colors.gray[600],
  borderColor: colors.gray[200],
  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
  "&:hover:not(:disabled)": {
    borderColor: colors.gray[300],
    backgroundColor: colors.gray[50],
  },
};

const compactSaveButtonSx = {
  ...compactActionButtonSx,
  backgroundColor: colors.orange.base,
  color: "#fff",
  "&:hover:not(:disabled)": {
    backgroundColor: colors.orange[600],
    boxShadow: `0 8px 24px ${colors.orange[200]}`,
  },
  "&:disabled": {
    opacity: 0.6,
    color: "#fff",
  },
};

const getAvailableTimeZones = (): string[] => {
  try {
    if (typeof Intl !== "undefined" && "supportedValuesOf" in Intl) {
      return (
        Intl as typeof Intl & {
          supportedValuesOf: (key: string) => string[];
        }
      ).supportedValuesOf("timeZone");
    }
  } catch (error) {
    console.error("Could not read supported time zones", error);
  }

  return [
    "UTC",
    "America/Merida",
    "America/Mexico_City",
    "America/Cancun",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Madrid",
    "Europe/Paris",
  ];
};

export const TimezoneConfigModalContent = ({ initialValue, onSave, onCancel, saving = false }: ITimezoneConfigModalContentProps) => {
  const timeZones = useMemo(() => getAvailableTimeZones(), []);

  const guessedTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch (error) {
      return "UTC";
    }
  }, []);

  const [selectedTimeZone, setSelectedTimeZone] = useState<string | null>(initialValue || guessedTimeZone || null);

  const localTimePreview = useMemo(() => {
    try {
      return DateTime.now().setZone(guessedTimeZone).toFormat("dd LLL yyyy, HH:mm ZZZZ");
    } catch (error) {
      return "";
    }
  }, [guessedTimeZone]);

  const selectedPreview = useMemo(() => {
    if (!selectedTimeZone) return "";

    try {
      return DateTime.now().setZone(selectedTimeZone).toFormat("dd LLL yyyy, HH:mm ZZZZ");
    } catch (error) {
      return "";
    }
  }, [selectedTimeZone]);

  const isValidSelection = !!selectedTimeZone && timeZones.includes(selectedTimeZone);

  const handleSave = async () => {
    if (!selectedTimeZone || !isValidSelection) return;
    await onSave({ timeZone: selectedTimeZone });
  };

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        backgroundColor: "transparent",
        width: "100%",
        maxWidth: 620,
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: colors.gray.base,
              lineHeight: 1.2,
            }}
          >
            Configure company time zone
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontSize: "0.88rem",
              color: colors.gray[500],
            }}
          >
            Used for scheduling, reminders, and date calculations.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 1.25,
            borderRadius: "12px",
            backgroundColor: colors.orange[50] || "var(--fx-surface-sunken, #F8FAFC)",
            border: `1px solid ${colors.orange[100] || "#ffd7ad"}`,
          }}
        >
          <Box sx={compactInfoPillSx}>
            <Public sx={{ fontSize: 16, color: colors.orange.base }} />
            <Typography sx={{ fontSize: "0.82rem", color: colors.gray[500], fontWeight: 600 }}>Local:</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: colors.gray.base, fontWeight: 500 }}>{guessedTimeZone}</Typography>
          </Box>

          <Box sx={compactInfoPillSx}>
            <AccessTime sx={{ fontSize: 16, color: colors.orange.base }} />
            <Typography sx={{ fontSize: "0.82rem", color: colors.gray[500], fontWeight: 600 }}>Now:</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: colors.gray.base, fontWeight: 500 }}>{localTimePreview}</Typography>
          </Box>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: "stretch" }}>
          <Box sx={{ flex: 1.2, minWidth: 0 }}>
            <Autocomplete
              options={timeZones}
              value={selectedTimeZone}
              onChange={(_, value) => setSelectedTimeZone(value)}
              fullWidth
              autoHighlight
              disablePortal
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company time zone"
                  placeholder="Select a time zone"
                  sx={compactAutocompleteSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Language sx={{ color: colors.orange.base, fontSize: 18 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="Preview"
              value={selectedPreview}
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTime sx={{ color: colors.orange.base, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Selected time preview"
              sx={compactInputSx}
            />
          </Box>
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.25,
            pt: 0.5,
          }}
        >
          <Button onClick={handleSave} disabled={!isValidSelection || saving} variant="contained" sx={compactSaveButtonSx}>
            {saving ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1, color: "white" }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};

export const CompanyTimezoneModalContainer = ({ initialTimeZone, onSaved, onClose }: ICompanyTimezoneModalContainerProps) => {
  const updateTimeZoneMutation = useUpdateCompany();

  const handleSave = async (payload) => {
    await updateTimeZoneMutation.mutateAsync(payload).then();
    onSaved?.(payload);
    
  };

  return (
    <Box sx={{ py: 1 }}>
      {updateTimeZoneMutation.isError && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: "10px",
            alignItems: "center",
            "& .MuiAlert-icon": {
              color: colors.danger.base,
            },
          }}
        >
          Could not save the company time zone.
        </Alert>
      )}

      <TimezoneConfigModalContent initialValue={initialTimeZone} onSave={handleSave} onCancel={onClose} saving={updateTimeZoneMutation.isPending} />
    </Box>
  );
};
